import { ErrorOutline } from '@mui/icons-material';
import { Alert, Box, useMediaQuery, useTheme } from '@mui/material';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import FunctionalDialog from 'components/Modals/FunctionalDialog';
import { SessionFlow } from 'services/api/entities';
import { getHomePageUrl } from 'services/helpers/domain';
import { useStripeSession, useSubscription } from 'services/hooks';
import { locations } from 'services/routes/locations';
import {
  StyledButton,
  StyledButtonContainer,
  StyledContainer,
  StyledHeader,
} from './styled';
import SubscriptionInfo from './components/SubscriptionInfo/SubscriptionInfo';
import UpgradeBanner from './components/UpgradeBanner';
import AccountLimitsHeader from './components/AccountLimitsHeader';
import AccountLimitsTable from './components/AccountLimitsTable';

const Subscription = (): ReactElement => {
  const theme = useTheme();
  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));
  const { error: stripeError, createUserPortalSession } = useStripeSession();

  const {
    isFreeAccount,
    subscription,
    isSubscriptionActive,
    isSubscriptionCancellationRequested,
  } = useSubscription();

  const navigate = useNavigate();
  const returnUrl = `${getHomePageUrl()}${locations.account.overview.pathname}`;

  const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] =
    useState<boolean>(false);

  const shouldShowCancelSubscriptionButton = useMemo(() => {
    if (!subscription || isFreeAccount) {
      return false;
    }
    return !isSubscriptionCancellationRequested;
  }, [subscription, isSubscriptionCancellationRequested, isFreeAccount]);

  const handleManageSubscriptionClick = useCallback(() => {
    if (isSubscriptionActive) {
      createUserPortalSession({ returnUrl });
    } else {
      navigate(locations.subscription.pricing.pathname(''));
    }
  }, [createUserPortalSession, navigate, isSubscriptionActive, returnUrl]);

  const handleProceedWithCancelSubscription = useCallback(
    () => createUserPortalSession({ returnUrl, flowType: SessionFlow.CANCEL }),
    [createUserPortalSession, returnUrl]
  );

  return stripeError ? (
    <Box mt={10}>
      <Alert
        variant="outlined"
        severity="error"
        color="error"
        data-testid="errorAlert"
        icon={<ErrorOutline color="error" />}
      >
        {stripeError && stripeError.message}
      </Alert>
    </Box>
  ) : (
    <StyledContainer>
      <StyledHeader data-testid="header" variant={isMobile ? 'h3' : 'h1'}>
        {t('account:subscription:subscription')}
      </StyledHeader>
      {isFreeAccount && <UpgradeBanner />}
      <SubscriptionInfo />
      <AccountLimitsHeader />
      <AccountLimitsTable />
      <StyledButtonContainer>
        {shouldShowCancelSubscriptionButton && (
          <StyledButton onClick={() => setIsCancelSubscriptionModalOpen(true)}>
            {t('account:subscription:cancel')}
          </StyledButton>
        )}
        <StyledButton variant="rounded" onClick={handleManageSubscriptionClick}>
          {!subscription || isFreeAccount
            ? t('account:subscription:viewPricingAndPlans')
            : t('account:subscription:manage')}
        </StyledButton>
        <FunctionalDialog
          title={t('account:subscription:cancelSubscriptionModal:title')}
          description={t(
            'account:subscription:cancelSubscriptionModal:description'
          )}
          closeText={t('account:subscription:cancelSubscriptionModal:dismiss')}
          submitText={t(
            'account:subscription:cancelSubscriptionModal:continue'
          )}
          state={{
            isOpen: isCancelSubscriptionModalOpen,
            isLoading: false,
            error: null,
          }}
          action={{
            onSubmit: handleProceedWithCancelSubscription,
            onClose: () => setIsCancelSubscriptionModalOpen(false),
            onExited: () => setIsCancelSubscriptionModalOpen(false),
          }}
        />
      </StyledButtonContainer>
    </StyledContainer>
  );
};

export default Subscription;
